import React, { useContext, useEffect, useRef, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import Pagination from "react-js-pagination";
import { NoteState } from "../../context/notes/NoteState";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

const emptyForm = (branchId, physioId = "") => ({
  client_id: "",
  client_name: "",
  gender: "",
  age: "",
  region: "",
  diagnosis: "",
  chief_complaint: "",
  pain_site: "",
  aggravating_factor: "",
  relieving_factor: "",
  radiating: "",
  pain_type: "",
  pain_score: "",
  functional_limitation: "",
  treatment_plan: "",
  goals: "",
  recommendations: "",
  session_recommended: "",
  prescription_date: moment().format("YYYY-MM-DD"),
  notes: "",
  branch_id: branchId,
  physio_id: physioId,
});

export default function PhysioPrescriptions() {
  const { state } = useContext(NoteState);
  const { branch_id, role, UId } = state;
  const isPhysioUser = role === "15";
  const defaultPhysioId = isPhysioUser ? UId : "";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm(branch_id, defaultPhysioId));
  const [search, setSearch] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [limit, setLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const [clientSearch, setClientSearch] = useState("");
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  useEffect(() => {
    fetchRows(1, limit);
    fetchClients();
    fetchPhysios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch_id]);

  useEffect(() => {
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchRows = async (page = 1, lim = limit, override = {}) => {
    if (!branch_id) return;
    setLoading(true);
    try {
      const res = await UserServices.getPhysioPrescriptions({
        branch_id,
        limit: lim,
        page,
        physio_id: isPhysioUser ? UId : override.physio_id || undefined,
        search: (override.search ?? search) || undefined,
        start_date: (override.start_date ?? filterStartDate) || undefined,
        end_date: (override.end_date ?? filterEndDate) || undefined,
      });
      if (res.status === 200 && res.data.status) {
        setRows(res.data.data || []);
        if (res.data.pagination) {
          setCurrentPage(res.data.pagination.current_page || 1);
          setTotalResults(res.data.pagination.total_record || 0);
        } else {
          setCurrentPage(1);
          setTotalResults((res.data.data || []).length);
        }
      }
    } catch (err) {
      console.log(err);
      setRows([]);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const res = await UserServices.getClientsName(branch_id);
      if (res.status === 200) {
        const list = (res.data.data || []).map((c) => ({
          id: c.id,
          name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
          phone: c.phone,
          gender: c.gender,
          age: c.birthday ? moment().diff(moment(c.birthday), "years") : "",
        }));
        setClients(list);
        setFilteredClients(list);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPhysios = async () => {
    try {
      const res = await UserServices.getPhysioUsers(branch_id);
      if (res.status === 200 && res.data.status) {
        setPhysios(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openAdd = () => {
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setClientSearch("");
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelected(row);
    setFormData({
      client_id: row.client_id || "",
      client_name: row.client_name || "",
      gender: row.gender || "",
      age: row.age || "",
      region: row.region || "",
      diagnosis: row.diagnosis || "",
      chief_complaint: row.chief_complaint || "",
      pain_site: row.pain_site || "",
      aggravating_factor: row.aggravating_factor || "",
      relieving_factor: row.relieving_factor || "",
      radiating: row.radiating || "",
      pain_type: row.pain_type || "",
      pain_score: row.pain_score ?? "",
      functional_limitation: row.functional_limitation || "",
      treatment_plan: row.treatment_plan || "",
      goals: row.goals || "",
      recommendations: row.recommendations || "",
      session_recommended: row.session_recommended || "",
      prescription_date: row.prescription_date
        ? moment(row.prescription_date).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      notes: row.notes || "",
      branch_id: row.branch_id || branch_id,
      physio_id: row.physio_id || defaultPhysioId,
    });
    setClientSearch(row.client_name || "");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setClientSearch("");
    setShowClientDropdown(false);
    setSaving(false);
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientSearch = (e) => {
    const v = e.target.value;
    setClientSearch(v);
    setShowClientDropdown(true);
    setFilteredClients(
      !v.trim()
        ? clients
        : clients.filter(
            (c) =>
              c.name.toLowerCase().includes(v.toLowerCase()) ||
              (c.phone && c.phone.includes(v))
          )
    );
  };

  const handleClientSelect = async (client) => {
    setClientSearch(client.name);
    setShowClientDropdown(false);
    setFormData((prev) => ({
      ...prev,
      client_id: client.id,
      client_name: client.name,
    }));
    try {
      const res = await UserServices.getPhysioAppointmentClientDetails(client.id);
      if (res.status === 200 && res.data.status) {
        const d = res.data.data || {};
        setFormData((prev) => ({
          ...prev,
          client_id: d.id || client.id,
          client_name: d.client_name || client.name,
          gender: d.gender || prev.gender,
          age: d.age || prev.age,
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!formData.client_name.trim()) {
      alert("Client name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        pain_score: formData.pain_score === "" ? null : Number(formData.pain_score),
        age: formData.age === "" ? null : Number(formData.age),
        client_id: formData.client_id || null,
        physio_id: isPhysioUser ? UId : formData.physio_id || null,
      };
      if (editMode && selected) {
        await UserServices.updatePhysioPrescription(selected.id, payload);
      } else {
        await UserServices.createPhysioPrescription(payload);
      }
      closeModal();
      fetchRows(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert(err?.response?.data?.message || "Failed to save prescription");
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this prescription?")) return;
    try {
      await UserServices.deletePhysioPrescription(id);
      fetchRows(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert("Failed to delete");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setFilterStartDate("");
    setFilterEndDate("");
    fetchRows(1, limit, { search: "", start_date: "", end_date: "" });
  };

  const handleExcel = async () => {
    try {
      const res = await UserServices.exportPhysioPrescriptions({
        branch_id,
        physio_id: isPhysioUser ? UId : undefined,
        search: search || undefined,
        start_date: filterStartDate || undefined,
        end_date: filterEndDate || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Physio_Prescriptions_${moment().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("Failed to export Excel");
    }
  };

  const downloadSinglePrescriptionPdf = (row) => {
    const doc = new jsPDF("p", "mm", "a4");
    doc.setFontSize(17);
    doc.text("Physio Consultancy", 14, 16);
    doc.setFontSize(10);
    doc.text(`Date: ${row.prescription_date ? moment(row.prescription_date).format("DD/MM/YYYY") : "-"}`, 14, 23);
    doc.text(`Client: ${row.client_name || "-"}`, 14, 30);
    doc.text(`Age/Gender: ${row.age || "-"} / ${row.gender || "-"}`, 14, 36);
    doc.text(`Diagnosis: ${row.diagnosis || "-"}`, 14, 42);
    doc.text(`Region: ${row.region || "-"}`, 14, 48);
    doc.text(`Session Recommended: ${row.session_recommended || "-"}`, 14, 54);
    doc.text(`Pain Score: ${row.pain_score ?? "-"}`, 14, 60);

    const lines = [
      ["C/O", row.chief_complaint],
      ["Pain Site", row.pain_site],
      ["Aggravating Factor", row.aggravating_factor],
      ["Relieving Factor", row.relieving_factor],
      ["Radiating", row.radiating],
      ["Type", row.pain_type],
      ["Functional Limitation", row.functional_limitation],
      ["Treatment Plan", row.treatment_plan],
      ["Goals", row.goals],
      ["Recommendations", row.recommendations],
      ["Notes", row.notes],
    ];

    let y = 68;
    doc.setFontSize(10);
    lines.forEach(([label, value]) => {
      const text = value || "-";
      doc.setFont(undefined, "bold");
      doc.text(`${label}:`, 14, y);
      doc.setFont(undefined, "normal");
      const split = doc.splitTextToSize(text, 150);
      doc.text(split, 55, y);
      y += Math.max(6, split.length * 5);
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    });

    doc.save(`Physio_Prescription_${(row.client_name || "client").replace(/[^\w\-]+/g, "_")}.pdf`);
  };

  const handleListPdf = () => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Physio Prescriptions", 14, 16);
      doc.autoTable({
        startY: 22,
        head: [["S#", "Date", "Client", "Diagnosis", "Region", "Pain", "Session", "Treatment Plan"]],
        body: rows.map((r, i) => [
          (currentPage - 1) * limit + i + 1,
          r.prescription_date ? moment(r.prescription_date).format("DD/MM/YY") : "",
          r.client_name || "",
          r.diagnosis || "",
          r.region || "",
          r.pain_score ?? "",
          r.session_recommended || "",
          r.treatment_plan || "",
        ]),
        headStyles: { fillColor: [69, 39, 160], textColor: 255 },
        styles: { fontSize: 8 },
      });
      doc.save(`Physio_Prescriptions_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Failed to generate PDF");
    }
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="page-heading d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">
            <i className="bi bi-clipboard2-pulse me-2"></i>
            Physio Prescriptions
          </h3>
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="bi bi-plus-circle me-1"></i>Add Prescription
          </button>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Search</label>
                <input
                  className="form-control"
                  placeholder="Client, diagnosis, region..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-4 d-flex gap-2">
                <button className="btn btn-primary" onClick={() => fetchRows(1, limit)}>Search</button>
                <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
                <button className="btn btn-success" onClick={handleExcel}>Excel</button>
                <button className="btn btn-danger" onClick={handleListPdf}>PDF</button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">No prescriptions found.</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>S#</th>
                        <th>Date</th>
                        <th>Client</th>
                        <th>Diagnosis</th>
                        <th>Region</th>
                        <th>Pain</th>
                        <th>Session</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.id}>
                          <td>{(currentPage - 1) * limit + i + 1}</td>
                          <td>{r.prescription_date ? moment(r.prescription_date).format("DD/MM/YY") : "—"}</td>
                          <td>{r.client_name || "—"}</td>
                          <td>{r.diagnosis || "—"}</td>
                          <td>{r.region || "—"}</td>
                          <td>{r.pain_score ?? "—"}</td>
                          <td>{r.session_recommended || "—"}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={() => openEdit(r)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="btn btn-outline-success" onClick={() => downloadSinglePrescriptionPdf(r)}>
                                <i className="bi bi-file-earmark-pdf"></i>
                              </button>
                              <button className="btn btn-outline-danger" onClick={() => handleDelete(r.id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalResults > limit ? (
                  <div className="d-flex justify-content-center">
                    <Pagination
                      activePage={currentPage}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalResults}
                      pageRangeDisplayed={5}
                      onChange={(p) => {
                        setCurrentPage(p);
                        fetchRows(p, limit);
                      }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <Modal show={showModal} onHide={closeModal} size="xl" backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? "Edit Prescription" : "Add Prescription"}</Modal.Title>
          </Modal.Header>
          <form onSubmit={submit}>
            <Modal.Body>
              <div className="row g-3">
                {!isPhysioUser ? (
                  <div className="col-md-4">
                    <label className="form-label">Physiotherapist</label>
                    <select
                      className="form-select"
                      name="physio_id"
                      value={formData.physio_id}
                      onChange={handleInput}
                      required
                    >
                      <option value="">Select physiotherapist</option>
                      {physios.map((p) => (
                        <option key={p.id} value={p.id}>
                          {`${p.first_name || ""} ${p.last_name || ""}`.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div className="col-md-8">
                  <label className="form-label">Select Insider Client (optional)</label>
                  <div className="position-relative" ref={clientDropdownRef}>
                    <input
                      className="form-control"
                      placeholder="Search client by name/phone"
                      value={clientSearch}
                      onChange={handleClientSearch}
                      onFocus={() => {
                        setShowClientDropdown(true);
                        setFilteredClients(clients);
                      }}
                    />
                    {showClientDropdown && filteredClients.length > 0 ? (
                      <div
                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                        style={{ maxHeight: 220, overflowY: "auto", zIndex: 1050 }}
                      >
                        {filteredClients.slice(0, 50).map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className="btn btn-link text-start w-100 border-bottom text-decoration-none"
                            onClick={() => handleClientSelect(c)}
                          >
                            <div className="fw-semibold text-dark">{c.name}</div>
                            <small className="text-muted">{c.phone || ""}</small>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="prescription_date"
                    value={formData.prescription_date}
                    onChange={handleInput}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Client Name</label>
                  <input
                    className="form-control"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInput}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Age</label>
                  <input
                    type="number"
                    className="form-control"
                    name="age"
                    value={formData.age}
                    onChange={handleInput}
                    min={1}
                    max={120}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Gender</label>
                  <input
                    className="form-control"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Region</label>
                  <input
                    className="form-control"
                    name="region"
                    value={formData.region}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Diagnosis</label>
                  <input
                    className="form-control"
                    name="diagnosis"
                    value={formData.diagnosis}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">C/O (Chief Complaint)</label>
                  <input
                    className="form-control"
                    name="chief_complaint"
                    value={formData.chief_complaint}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Pain Site</label>
                  <input
                    className="form-control"
                    name="pain_site"
                    value={formData.pain_site}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Pain Type</label>
                  <input
                    className="form-control"
                    name="pain_type"
                    value={formData.pain_type}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Pain Score (0-10)</label>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    className="form-control"
                    name="pain_score"
                    value={formData.pain_score}
                    onChange={handleInput}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Aggravating Factor</label>
                  <input
                    className="form-control"
                    name="aggravating_factor"
                    value={formData.aggravating_factor}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Relieving Factor</label>
                  <input
                    className="form-control"
                    name="relieving_factor"
                    value={formData.relieving_factor}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Radiating</label>
                  <input
                    className="form-control"
                    name="radiating"
                    value={formData.radiating}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Session Recommended</label>
                  <input
                    className="form-control"
                    name="session_recommended"
                    value={formData.session_recommended}
                    onChange={handleInput}
                    placeholder="e.g. consultation / 6 / 10 sessions"
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label">Functional Limitation</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="functional_limitation"
                    value={formData.functional_limitation}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Treatment Plan</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="treatment_plan"
                    value={formData.treatment_plan}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Goals</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="goals"
                    value={formData.goals}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Recommendations</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="recommendations"
                    value={formData.recommendations}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInput}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : editMode ? "Update" : "Save"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </div>
  );
}
